import React, { useRef, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import gsap from 'gsap';

// Predefined color sequences for the animations
const COLOR_CONFIG = {
  primary: {
    steps: ['#212bc4', '#212a8e', '#1E1E20', '#212bc4'],
    baseColor: '#212bc4', // Base color for primary to avoid flickering
  },
  secondary: {
    steps: ['#1E1E20', '#212bc4', '#212a8e', '#1E1E20'],
    baseColor: '#1E1E20', // Base color for secondary
  },
};

// Predefined animation timings to avoid recalculations
const ANIMATION_TIMINGS = [
  [0, 0], // First color (base, no animation)
  [0.5, 0.2], // Second color: duration 0.5s, starts at 0.2s
  [0.2, 0.5], // Third color: duration 0.2s, starts at 0.5s
  [0.9, 0.3], // Last color: duration 0.9s, starts at 0.3s
];

// Max number of DOM elements to create
const MAX_OVERLAYS = 3;

const AnimatingTag = memo(({ className, label, type }) => {
  const tagRef = useRef(null);
  const animation = useRef(null);
  const overlaysRef = useRef([]);
  
  // Memoize the colors based on type to prevent recalculation
  const colorSequence = useMemo(() => {
    return type === 'primary' ? COLOR_CONFIG.primary.steps : COLOR_CONFIG.secondary.steps;
  }, [type]);
  
  const baseColor = useMemo(() => {
    return type === 'primary' ? COLOR_CONFIG.primary.baseColor : COLOR_CONFIG.secondary.baseColor;
  }, [type]);

  // Setup animation only when type changes
  useEffect(() => {    
    // Cancel any existing animations to prevent memory leaks
    if (animation.current) {
      animation.current.kill();
    }

    const element = tagRef.current;
    if (!element) return;

    // Generate random delays for more visible randomness 
    const initialDelay = Math.random() * 3; // Random start delay between 0-3 seconds
    const loopDelay = 1 + Math.random() * 5; // Random loop delay between 1-6 seconds

    // Create a new GSAP timeline with infinite repeat
    animation.current = gsap.timeline({
      repeat: -1, // Infinite loop
      repeatDelay: loopDelay,
      delay: initialDelay,
    });

    // Apply base color
    gsap.set(element, {
      backgroundColor: colorSequence[0],
    });

    // Create and cache overlay elements if they don't exist
    if (overlaysRef.current.length === 0) {
      for (let i = 0; i < MAX_OVERLAYS; i++) {
        const overlay = document.createElement('div');
        overlay.className = 'absolute top-0 left-0 w-full h-full';
        overlay.style.transform = 'scaleX(0)';
        overlay.style.transformOrigin = 'left';
        overlay.style.pointerEvents = 'none';
        element.appendChild(overlay);
        overlaysRef.current.push(overlay);
      }
    }

    // Apply color to overlays and create animations - slightly randomized for each instance
    for (let i = 1; i < colorSequence.length && i <= MAX_OVERLAYS; i++) {
      const overlay = overlaysRef.current[i-1];
      if (!overlay) continue;
      
      // Set color
      overlay.style.backgroundColor = colorSequence[i];
      
      // Get animation timing with slight randomness
      const baseDuration = ANIMATION_TIMINGS[i][0];
      const baseStartPosition = ANIMATION_TIMINGS[i][1];
      
      // Add small random variations to make each instance unique
      const duration = baseDuration * (0.9 + Math.random() * 0.2); // ±10% variation
      const startPosition = baseStartPosition * (0.95 + Math.random() * 0.1); // ±5% variation

      // Create animations with optimized parameters
      animation.current
        .to(overlay, {
          scaleX: 1,
          duration,
          ease: 'power1.inOut',
          force3D: true, // Hardware acceleration
        }, startPosition)
        .to(overlay, {
          scaleX: 0,
          transformOrigin: 'right',
          duration,
          ease: 'power1.inOut',
          force3D: true,
        }, startPosition + duration + 0.3)
        .set(overlay, {
          transformOrigin: 'left',
        }, startPosition + duration * 2 + 0.3);
    }

    // Cleanup function
    return () => {
      if (animation.current) {
        animation.current.kill();
        animation.current = null;
      }
      // We don't remove overlays on unmount to reuse them when component remounts
    };
  }, [colorSequence]);

  // Clean up all DOM elements on component unmount
  useEffect(() => {
    return () => {
      // Remove all created overlay elements when component fully unmounts
      overlaysRef.current.forEach(overlay => {
        if (overlay && overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      });
      overlaysRef.current = [];
    };
  }, []);
  
  // Force animation restart on window focus to ensure animations are visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && animation.current) {
        // Restart the animation with new random timing
        animation.current.restart(true, false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Checkmark SVG memoized to prevent re-renders
  const checkmarkSvg = useMemo(() => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="14"
      viewBox="0 0 16 14"
      fill="none"
    >
      <rect
        x="2.90527"
        y="6.63962"
        width="7.30983"
        height="2.65507"
        transform="rotate(32.0828 2.90527 6.63962)"
        fill="white"
      />
      <rect
        x="14"
        y="2.72739"
        width="11.6823"
        height="2.65507"
        transform="rotate(122.083 14 2.72739)"
        fill="white"
      />
    </svg>
  ), []);

  return (
    <div className={className}>
      <div
        ref={tagRef}
        className="relative flex w-max items-center gap-2 overflow-hidden p-2 text-sm text-white will-change-transform"
        style={{
          backgroundColor: baseColor,
          transition: 'none', // Disable CSS transitions
        }}
      >
        {/* Content that stays on top of the animation */}
        <div className="relative z-10 flex items-center gap-2">
          {label}
          {checkmarkSvg}
        </div>
      </div>
    </div>
  );
});

AnimatingTag.displayName = 'AnimatingTag';

AnimatingTag.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.oneOf(['primary', 'secondary']),
};

export default AnimatingTag;
